import Loader from "@/components/Common/kpi/Loader";
import clsxm from "@/lib/clsxm";
import {ButtonHTMLAttributes, FC, PropsWithChildren, memo, useId} from "react";
import {Tooltip} from "react-tooltip";

const Button: FC<
  PropsWithChildren & {
    buttonStyle?: "Blue" | "Custom" | "Danger" | "Gradient" | "Green" | "White";
    onClick?(): void;
    className?: string;
    isDisabled?: boolean;
    isLoading?: boolean;
    disabledTooltipText?: string;
    shouldScaleOnHover?: boolean;
    shouldPreventDefault?: boolean;
    shouldStopPropagation?: boolean;
    customButtonId?: string;
  } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled">
> = ({
  buttonStyle = "Custom",
  className,
  shouldScaleOnHover = true,
  shouldPreventDefault = true,
  shouldStopPropagation = true,
  isDisabled,
  children,
  onClick,
  isLoading,
  disabledTooltipText,
  customButtonId,
  ...rest
}) => {
  const buttonId = useId();
  const id = useId();
  return (
    <>
      <button
        id={customButtonId || buttonId}
        className={clsxm(
          "relative flex h-14 cursor-pointer items-center justify-center rounded-md p-2 text-base font-medium transition-all duration-150 enabled:hover:shadow-xs disabled:cursor-not-allowed",
          buttonStyle === "Gradient" &&
            "disabled:bg-neutral-grey-light-dark disabled:text-neutral-grey-light enabled:bg-linear-(--button-gradient) enabled:text-white enabled:active:scale-95",
          buttonStyle === "Blue" &&
            "bg-primary-dark-solid enabled:hover:bg-primary-medium-solid disabled:bg-neutral-white-dark disabled:text-neutral-grey-light enabled:text-white enabled:active:scale-95",
          buttonStyle === "Green" &&
            "bg-success-medium-green enabled:hover:bg-success-herbal disabled:bg-neutral-white-dark disabled:text-neutral-grey-light enabled:text-white enabled:active:scale-95",
          buttonStyle === "White" &&
            "border-primary-light-solid enabled:text-primary-dark-solid enabled:hover:bg-primary-light-solid enabled:hover:text-primary-medium-solid disabled:bg-neutral-white-dark disabled:text-neutral-grey-light bg-white enabled:border enabled:active:scale-95",
          buttonStyle === "Danger" &&
            "bg-danger-medium-red enabled:hover:bg-danger-medium-red/80 disabled:bg-neutral-white-dark disabled:text-neutral-grey-light enabled:text-white enabled:active:scale-95",
          shouldScaleOnHover && buttonStyle !== "Custom" && "enabled:hover:scale-[1.01]",
          className,
        )}
        type="button"
        onClick={e => {
          if (shouldPreventDefault) e.preventDefault();
          if (shouldStopPropagation) e.stopPropagation();
          onClick && onClick();
        }}
        disabled={isDisabled || isLoading || !onClick}
        {...rest}>
        {isLoading ? <Loader /> : children}
        {isDisabled && !!disabledTooltipText && (
          <div className="absolute inset-0 -z-0" data-tip data-tooltip-id={`button-${id}`} />
        )}
      </button>
      {!!disabledTooltipText && isDisabled && (
        <Tooltip id={`button-${id}`} place="bottom" variant="dark" className="z-50">
          {disabledTooltipText}
        </Tooltip>
      )}
    </>
  );
};

export default memo(Button);
